import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useCapabilities } from "../lib/useCapabilities";

/**
 * BTC3 Phase 4 · Frontend lifecycle & holding-state guard.
 *
 * Consumes the SAME authoritative signals as the backend Phase 4
 * shared dependency:
 *   - `useAuth()`           — central auth client, single source
 *                             of session state.  Never reads the
 *                             obsolete localStorage token path.
 *   - `useCapabilities()`   — derived from `/api/experience/resolve`
 *                             (the Phase 2 Experience Resolver, which
 *                             itself calls `derive_activation`).  The
 *                             `northstar_access` capability bit is the
 *                             frontend projection of the same
 *                             `northstar_access_granted` predicate the
 *                             backend enforces.
 *   - `holdingState`        — `"matching_delay"` when the patient is
 *                             held in MATCHING_DELAY.  A patient in
 *                             this state is redirected to /portal/holding
 *                             consistent with the Experience Resolver's
 *                             landing_route for the same lifecycle.
 *
 * The guard IS NOT the security authority — every guarded backend
 * endpoint enforces the same predicate through
 * `lifecycle_dependency.require_northstar_access`.  A user who
 * enters a protected URL directly cannot bypass the backend gate,
 * even if this guard were somehow skipped.
 */
export default function LifecycleGuard({ children }) {
  const { user, loading } = useAuth();
  const { caps, ready, holdingState } = useCapabilities();
  const location = useLocation();

  if (loading || !ready) {
    return (
      <div
        data-testid="lifecycle-guard-loading"
        className="min-h-screen bg-[#0F1720] text-[#F4EEE0] flex items-center justify-center text-sm"
      >
        Loading…
      </div>
    );
  }

  if (!user) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: location.pathname + location.search }}
      />
    );
  }

  // Phase 4 · MATCHING_DELAY holding view is the same route the
  // Experience Resolver returns as landing_route for a ready-for-
  // matching patient past the 72h threshold.  Route directly there.
  // Northstar access is never Buddy-gated: granted access wins over holding.
  if (caps.northstar_access) return children;

  if (holdingState === "matching_delay") {
    return <Navigate to="/portal/holding" replace />;
  }

  return <Navigate to="/onboarding" replace />;
}
